/** Civilités ServiceBox (clientDto.civiliteId, lues sur le formulaire RDV le 2026-10-15). */
const SB_CIVILITES: Record<string, string> = {
  M: "1", MR: "1", MONSIEUR: "1", MME: "2", MADAME: "2",
  MLE: "3", MLLE: "3", MADEMOISELLE: "3", SOC: "4", SOCIETE: "4",
};

const norm = (raw?: string | null) =>
  (raw || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().trim();

/** Nom, prénom et civiliteId du client tel que ServiceBox et Alpha l'impriment — même
 *  règle que sb_pretransfer.sb_client_identity. Avant, seuls « M. », « Mme » et « Mlle »
 *  étaient reconnus : « Société », « Mme. » ou « Mle. » partaient en « M. ». Et le dossier
 *  d'une société dont le contact principal est le conducteur sortait au nom du conducteur
 *  (« MR … », Héric 2026-10-15) : c'est alors la société qui est le client. */
export function sbClientIdentity(
  dossier: { type?: string | null; name?: string | null } | null | undefined,
  contact: { nom?: string | null; prenom?: string | null; civilite?: string | null },
  fallbackName = "",
): { nom: string; prenom: string; civilite_id: string } {
  const nom = contact.nom || fallbackName || "";
  const prenom = contact.prenom || "";
  const civ = SB_CIVILITES[norm(contact.civilite).replace(/[^A-Z]/g, "")] || "";
  const company = (dossier?.name || "").trim();
  if (dossier?.type === "professionnel" && company) {
    if (civ === "4" || !prenom) return { nom: nom || company, prenom: "", civilite_id: "4" };
    if (!norm(company).includes(norm(contact.nom))) return { nom: company, prenom: "", civilite_id: "4" };
  }
  return { nom, prenom, civilite_id: civ || "1" };
}

/** civilite_id when Carvis really knows it — the contact's own civilité, or a company (4) — and
 *  "" when sbClientIdentity only fell back to M. Same rule as sb_pretransfer.known_civilite:
 *  the robot sets it on a client Alpha creates from the transfer (proxy >= 3.66.2). */
export function knownCivilite(contact: { civilite?: string | null } | null | undefined, civiliteId: string): string {
  if (civiliteId === "4") return "4";
  return SB_CIVILITES[norm(contact?.civilite).replace(/[^A-Z]/g, "")] === civiliteId ? civiliteId : "";
}

type SbContact = { nom?: string | null; prenom?: string | null; phone?: string | null; is_primary?: boolean | null };
const nameKey = (raw?: string | null) => norm(raw).replace(/[^A-Z0-9]/g, "");

/** Le contact du dossier choisi à la prise de RDV (`ordre.name`) quand ce n'est PAS le
 *  contact principal — même règle que sb_pretransfer.booked_contact. Le dossier d'une
 *  voiture de flotte a souvent le conducteur en principal et la société en second
 *  contact : le RDV pris au nom de la société partait au nom du conducteur (HD-176-VL,
 *  « M. COCHARD DAVID » au lieu d'« ALPHABET FR FLEET MANAGEMENT », Héric 2026-10-15). */
export function bookedContact<C extends SbContact>(contacts: C[] | null | undefined, bookedName?: string | null): C | null {
  const key = nameKey(bookedName);
  if (!key || !contacts?.length) return null;
  const primary = contacts.find((c) => c.is_primary) || contacts[0];
  for (const c of contacts) {
    const names = [nameKey(c.nom), nameKey(`${c.nom || ""} ${c.prenom || ""}`), nameKey(`${c.prenom || ""} ${c.nom || ""}`)];
    if (names.some((n) => n && n === key)) return c === primary ? null : c;
  }
  return null;
}

const isMobile = (phone: string) => /^(0[67]\d{8}|33[67]\d{8})$/.test(phone.replace(/\D/g, ""));

/** Le premier MOBILE des contacts (ServiceBox l'exige, SMS/WhatsApp y partent), sinon le
 *  premier téléphone — même règle que sb_pretransfer.sb_phone. */
export function sbPhone(...contacts: (SbContact | null | undefined)[]): string {
  const phones = contacts.map((c) => (c?.phone || "").replace(/\s/g, ""));
  return phones.find((p) => isMobile(p)) || phones.find((p) => p) || "";
}
